import * as Sentry from '@sentry/react'
import { track } from '@vercel/analytics'
import ReactGA from 'react-ga4'

import { classifyVersionCheckError } from './classifyVersionCheckError'
import type { TelemetrySink } from './VersionService'

/**
 * Só valores que o Vercel Analytics aceita como propriedade de evento.
 * Um `Error` no payload (do `version_check_failed`) quebraria o envio.
 */
const serializar = (payload: Record<string, unknown>) =>
  Object.entries(payload).reduce<Record<string, string | number | boolean>>(
    (acumulado, [chave, valor]) => {
      if (valor === null || valor === undefined) return acumulado

      acumulado[chave] =
        typeof valor === 'string' ||
        typeof valor === 'number' ||
        typeof valor === 'boolean'
          ? valor
          : String(valor)

      return acumulado
    },
    {}
  )

/**
 * Sink real de telemetria do ciclo de versão.
 *
 * Usa o pipeline que o `AppShell` de fato monta — ReactGA, Vercel Analytics e
 * Sentry. O `IntegrationsProvider` existe no código mas não está montado em
 * árvore nenhuma hoje; depender dele seria emitir para o vazio.
 *
 * O ponto de tudo isto é responder o que hoje ninguém consegue: quantos
 * participantes ficaram desatualizados, quantos reloads foram adiados por
 * fluxo crítico, e se o endpoint de versão está saudável. Antes, o
 * `catch (error) {}` do mecanismo anterior não deixava rastro nenhum.
 */
export const createTelemetrySink =
  (): TelemetrySink =>
  (event, payload = {}) => {
    const propriedades = serializar(payload)

    if (event === 'version_check_failed' && payload.error instanceof Error) {
      const failureKind = classifyVersionCheckError(payload.error)

      propriedades.failureKind = failureKind
      propriedades.errorMessage = payload.error.message
    }

    try {
      track(event, propriedades)
      ReactGA.event(event, propriedades)
    } catch {
      // Telemetria nunca derruba o app: um bloqueador de anúncios ou GA não
      // inicializado não pode impedir o serviço de versão de funcionar.
    }

    if (event === 'version_check_failed' && payload.error instanceof Error) {
      const failureKind = propriedades.failureKind as
        | 'transient-network'
        | 'server'

      // Client network blips stay in analytics; Sentry is for endpoint failures.
      if (failureKind === 'server')
        Sentry.captureException(payload.error, {
          tags: { feature: 'version-update', failureKind }
        })
    }
  }
